/**
 * Export content to PDF format.
 *
 * Uses pdf-lib to generate a clean PDF document from markdown-like content.
 * Structured `sheets` (and markdown tables) are drawn as real bordered tables
 * so they match the Word export rather than dumping pipe-separated text.
 */

import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { AI_DISCLAIMER } from "@cvx/lib/aiDisclaimer";
import { arrayBufferBytes } from "@cvx/lib/arrayBufferBytes";
import type { ExportContent, ExportSheet } from "./exportToWord";
import {
  outputToMarkdown,
  chatToMarkdown,
  formatSheetCell,
} from "./exportToWord";
import { renderChartsForExport } from "./exportCharts";

const PAGE_WIDTH = 595.28; // A4 portrait
const PAGE_HEIGHT = 841.89;
const MARGIN = 48;
const LINE_HEIGHT = 16;
const HEADING_SIZES = { h1: 20, h2: 16, h3: 14, h4: 12 };
const BODY_SIZE = 10;

/** Wide registers (creditors, etc.) need landscape or the columns crush. */
const LANDSCAPE_COL_THRESHOLD = 6;

const TABLE_FONT_SIZE = 8;
const TABLE_LINE_HEIGHT = 10;
const TABLE_PAD_X = 4;
const TABLE_PAD_Y = 3.5;
const TABLE_BORDER = rgb(0.75, 0.75, 0.75);
const TABLE_HEADER_FILL = rgb(0.94, 0.94, 0.94);
const TABLE_HEADER_FG = rgb(0.15, 0.15, 0.15);

interface DrawContext {
  doc: PDFDocument;
  page: ReturnType<PDFDocument["addPage"]>;
  y: number;
  pageWidth: number;
  pageHeight: number;
  font: Awaited<ReturnType<PDFDocument["embedFont"]>>;
  boldFont: Awaited<ReturnType<PDFDocument["embedFont"]>>;
  italicFont: Awaited<ReturnType<PDFDocument["embedFont"]>>;
}

function contentWidth(ctx: DrawContext): number {
  return ctx.pageWidth - MARGIN * 2;
}

function newPage(ctx: DrawContext): void {
  ctx.page = ctx.doc.addPage([ctx.pageWidth, ctx.pageHeight]);
  ctx.y = ctx.pageHeight - MARGIN;
}

function ensureSpace(ctx: DrawContext, needed: number): void {
  if (ctx.y - needed < MARGIN) {
    newPage(ctx);
  }
}

/**
 * Standard-font PDFs encode WinAnsi (cp1252) only. Characters outside it —
 * arrows, maths symbols, emoji in narrative text — either throw in pdf-lib
 * or render as tofu, both of which read as "the fonts are out of shape"
 * (MOR-978). Map the common offenders to ASCII and drop the rest.
 */
const WINANSI_EXTRAS = "€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ";
const NON_WINANSI = new RegExp(
  `[^\\u0009\\u000A\\u000D\\u0020-\\u007E\\u00A0-\\u00FF${WINANSI_EXTRAS}]`,
  "g",
);
const WINANSI_MAP: Record<string, string> = {
  "−": "-", // minus sign
  "≥": ">=",
  "≤": "<=",
  "→": "->",
  "←": "<-",
  "✓": "*",
  "✔": "*",
  "​": "", // zero-width space
};

function sanitizeWinAnsi(text: string): string {
  return text.replace(NON_WINANSI, (ch) => WINANSI_MAP[ch] ?? "");
}

/**
 * Wrap text to fit within a max width, returning lines.
 *
 * `firstWidth` lets the caller reserve room on the first line (used by the
 * "**Label:** value" renderer, which draws the label inline before the value).
 * Tokens that are wider than the line on their own - long file names, URLs,
 * unbroken reference strings - are hard-broken rather than run off the page.
 */
function wrapText(
  text: string,
  font: DrawContext["font"],
  fontSize: number,
  maxWidth: number,
  firstWidth: number = maxWidth,
): string[] {
  const fits = (s: string, width: number) =>
    font.widthOfTextAtSize(s, fontSize) <= width;

  /** Split a single over-long token into chunks that fit `width`. */
  const breakToken = (token: string, width: number): string[] => {
    const chunks: string[] = [];
    const lastChunk = [...token].reduce((chunk, ch) => {
      if (chunk && !fits(chunk + ch, width)) {
        chunks.push(chunk);
        return ch;
      }
      return chunk + ch;
    }, "");
    if (lastChunk) chunks.push(lastChunk);
    return chunks.length > 0 ? chunks : [token];
  };

  const lines: string[] = [];
  let currentLine = "";
  const widthFor = () => (lines.length === 0 ? firstWidth : maxWidth);

  for (const word of sanitizeWinAnsi(text).split(" ")) {
    if (!word) continue;
    // A token too wide even on an empty line has to be broken up.
    if (!fits(word, maxWidth)) {
      if (currentLine) lines.push(currentLine);
      const chunks = breakToken(word, widthFor());
      lines.push(...chunks.slice(0, -1));
      currentLine = chunks[chunks.length - 1] ?? "";
      continue;
    }
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (!fits(testLine, widthFor()) && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.length > 0 ? lines : [""];
}

function cellText(value: unknown): string {
  return sanitizeWinAnsi(formatSheetCell(value));
}

function drawText(
  ctx: DrawContext,
  text: string,
  options: {
    fontSize?: number;
    bold?: boolean;
    italic?: boolean;
    indent?: number;
    color?: { r: number; g: number; b: number };
    spacingAfter?: number;
  } = {},
): void {
  const fontSize = options.fontSize ?? BODY_SIZE;
  const font = options.bold
    ? ctx.boldFont
    : options.italic
      ? ctx.italicFont
      : ctx.font;
  const indent = options.indent ?? 0;
  const color = options.color
    ? rgb(options.color.r, options.color.g, options.color.b)
    : rgb(0, 0, 0);
  const spacingAfter = options.spacingAfter ?? 4;

  // Strip markdown bold/italic markers for PDF
  const cleanText = text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1");

  const lines = wrapText(cleanText, font, fontSize, contentWidth(ctx) - indent);
  for (const line of lines) {
    ensureSpace(ctx, LINE_HEIGHT);
    ctx.page.drawText(line, {
      x: MARGIN + indent,
      y: ctx.y,
      size: fontSize,
      font,
      color,
    });
    ctx.y -= LINE_HEIGHT;
  }
  ctx.y -= spacingAfter;
}

function drawHorizontalRule(ctx: DrawContext): void {
  ensureSpace(ctx, 20);
  ctx.y -= 8;
  ctx.page.drawLine({
    start: { x: MARGIN, y: ctx.y },
    end: { x: ctx.pageWidth - MARGIN, y: ctx.y },
    thickness: 0.5,
    color: rgb(0.8, 0.8, 0.8),
  });
  ctx.y -= 12;
}

function rowHeight(wrapped: string[][]): number {
  const maxLines = Math.max(1, ...wrapped.map((lines) => lines.length));
  return TABLE_PAD_Y * 2 + maxLines * TABLE_LINE_HEIGHT;
}

function wrapRow(
  ctx: DrawContext,
  cells: string[],
  colWidths: number[],
  bold: boolean,
): string[][] {
  const font = bold ? ctx.boldFont : ctx.font;
  return cells.map((cell, i) => {
    const inner = Math.max((colWidths[i] ?? 0) - TABLE_PAD_X * 2, 8);
    return wrapText(cell, font, TABLE_FONT_SIZE, inner);
  });
}

/** Section-label row fill — lighter than the header, still visibly grouped. */
const TABLE_SECTION_FILL = rgb(0.97, 0.97, 0.97);

/**
 * Distribute the table width by each column's widest content, so a label
 * column gets the room its text needs instead of an even split that
 * hard-breaks words mid-token (the MOR-978 "fonts out of shape" complaint).
 */
function computeColWidths(
  ctx: DrawContext,
  headers: string[],
  rows: Array<Array<string | number | Date | null | undefined>>,
  totalWidth: number,
): number[] {
  const natural = headers.map((h, i) => {
    const w = Math.max(
      ctx.boldFont.widthOfTextAtSize(cellText(h), TABLE_FONT_SIZE),
      ...rows.map((row) =>
        ctx.font.widthOfTextAtSize(cellText(row[i]), TABLE_FONT_SIZE),
      ),
    );
    // Floor keeps sparse columns clickable-narrow but legible; the cap stops
    // one narrative column (e.g. indicator analysis) starving the rest.
    return Math.min(Math.max(w + TABLE_PAD_X * 2 + 2, 34), totalWidth * 0.45);
  });
  const sum = natural.reduce((a, b) => a + b, 0);
  return natural.map((w) => (w / sum) * totalWidth);
}

/**
 * Draw a bordered table with a shaded header, wrapping cell text. Rows that
 * don't fit the remaining page start a new page with the header repeated —
 * same idea as Word's table, just drawn with pdf-lib. Numeric cells are
 * right-aligned; `sectionRows` indexes render bold on a light fill.
 */
function drawPdfTable(
  ctx: DrawContext,
  headers: string[],
  rows: Array<Array<string | number | Date | null | undefined>>,
  sectionRows?: Set<number>,
): void {
  const colCount = headers.length;
  if (colCount === 0) return;

  const colWidths = computeColWidths(ctx, headers, rows, contentWidth(ctx));

  const paintRow = (
    cells: string[],
    opts: { header?: boolean; section?: boolean; rightAligned?: boolean[] },
  ) => {
    const bold = !!opts.header || !!opts.section;
    const wrapped = wrapRow(ctx, cells, colWidths, bold);
    const height = rowHeight(wrapped);
    const rowBottom = ctx.y - height;
    const colX = colWidths.reduce<{ x: number; positions: number[] }>(
      (acc, w) => ({ x: acc.x + w, positions: [...acc.positions, acc.x] }),
      { x: MARGIN, positions: [] },
    ).positions;
    const initialTextY = ctx.y - TABLE_PAD_Y - TABLE_FONT_SIZE + 1;

    for (let i = 0; i < colCount; i++) {
      const colWidth = colWidths[i] ?? 0;
      const x = colX[i] ?? MARGIN;
      ctx.page.drawRectangle({
        x,
        y: rowBottom,
        width: colWidth,
        height,
        borderWidth: 0.4,
        borderColor: TABLE_BORDER,
        color: opts.header
          ? TABLE_HEADER_FILL
          : opts.section
            ? TABLE_SECTION_FILL
            : undefined,
      });
      const font = bold ? ctx.boldFont : ctx.font;
      for (const [lineIdx, line] of (wrapped[i] ?? [""]).entries()) {
        if (line) {
          const textY = initialTextY - lineIdx * TABLE_LINE_HEIGHT;
          const lineX = opts.rightAligned?.[i]
            ? x +
              colWidth -
              TABLE_PAD_X -
              font.widthOfTextAtSize(line, TABLE_FONT_SIZE)
            : x + TABLE_PAD_X;
          ctx.page.drawText(line, {
            x: lineX,
            y: textY,
            size: TABLE_FONT_SIZE,
            font,
            color: opts.header ? TABLE_HEADER_FG : rgb(0, 0, 0),
          });
        }
      }
    }
    ctx.y = rowBottom;
  };

  const drawHeader = () =>
    paintRow(
      headers.map((h) => cellText(h)),
      { header: true },
    );

  const fits = (cells: string[], bold: boolean) => {
    const height = rowHeight(wrapRow(ctx, cells, colWidths, bold));
    return ctx.y - height >= MARGIN;
  };

  if (
    !fits(
      headers.map((h) => cellText(h)),
      true,
    )
  )
    newPage(ctx);
  drawHeader();

  rows.forEach((row, rowIdx) => {
    const section = sectionRows?.has(rowIdx) ?? false;
    const cells = headers.map((_, i) => cellText(row[i]));
    if (!fits(cells, section)) {
      newPage(ctx);
      drawHeader();
    }
    paintRow(cells, {
      section,
      rightAligned: headers.map((_, i) => typeof row[i] === "number"),
    });
  });

  ctx.y -= 12;
}

function parseMarkdownTableRow(line: string): string[] {
  return line
    .slice(1, -1)
    .split("|")
    .map((c) => c.trim());
}

function pageDimsFor(content: ExportContent): {
  width: number;
  height: number;
} {
  if (content.sheets && content.sheets.length > 0) {
    const maxCols = Math.max(...content.sheets.map((s) => s.headers.length));
    if (maxCols >= LANDSCAPE_COL_THRESHOLD) {
      return { width: PAGE_HEIGHT, height: PAGE_WIDTH };
    }
  }
  return { width: PAGE_WIDTH, height: PAGE_HEIGHT };
}

/** Widest a chart is drawn, in points — roughly the portrait text column. */
const CHART_MAX_WIDTH_PT = 460;

/** Rasterise and place a sheet's charts under its table (MOR-978). */
async function drawSheetCharts(
  ctx: DrawContext,
  charts: ExportSheet["charts"],
): Promise<void> {
  for (const { chart, image } of await renderChartsForExport(charts)) {
    const png = await ctx.doc.embedPng(image.bytes);
    const w = Math.min(contentWidth(ctx), CHART_MAX_WIDTH_PT);
    const h = (w / image.width) * image.height;

    // Keep the caption and its chart on the same page.
    ensureSpace(ctx, LINE_HEIGHT + 8 + h);
    drawText(ctx, chart.title, { bold: true, spacingAfter: 4 });
    ensureSpace(ctx, h + 4);
    ctx.page.drawImage(png, { x: MARGIN, y: ctx.y - h, width: w, height: h });
    ctx.y -= h + 16;
  }
}

async function drawSheets(
  ctx: DrawContext,
  title: string,
  sheets: ExportSheet[],
): Promise<void> {
  drawText(ctx, title, {
    fontSize: HEADING_SIZES.h1,
    bold: true,
    spacingAfter: 14,
  });
  for (const sheet of sheets) {
    if (sheet.omitFromWordPdf) continue;
    // Same rule as Word: don't reprint a sheet name that duplicates the title.
    if (sheet.name.trim().toLowerCase() !== title.trim().toLowerCase()) {
      ctx.y -= 8;
      drawText(ctx, sheet.name, {
        fontSize: HEADING_SIZES.h2,
        bold: true,
        spacingAfter: 10,
      });
    }
    drawPdfTable(
      ctx,
      sheet.headers,
      sheet.rows,
      sheet.sectionRows ? new Set(sheet.sectionRows) : undefined,
    );
    await drawSheetCharts(ctx, sheet.charts);
  }
}

function drawMarkdown(ctx: DrawContext, markdown: string): void {
  const lines = markdown.split("\n");
  let tableRows: string[][] = [];

  const flushTable = () => {
    if (tableRows.length === 0) return;
    const headers = tableRows[0] ?? [];
    const body = tableRows.slice(1);
    drawPdfTable(ctx, headers, body);
    tableRows = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushTable();
      ctx.y -= 6;
      continue;
    }

    // Table separator or table row — accumulate like the Word exporter.
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      if (/^\|[\s\-:|]+\|$/.test(trimmed)) continue;
      tableRows.push(parseMarkdownTableRow(trimmed));
      continue;
    }

    flushTable();

    // Headings
    if (trimmed.startsWith("#### ")) {
      drawText(ctx, trimmed.slice(5), {
        fontSize: HEADING_SIZES.h4,
        bold: true,
        spacingAfter: 8,
      });
    } else if (trimmed.startsWith("### ")) {
      drawText(ctx, trimmed.slice(4), {
        fontSize: HEADING_SIZES.h3,
        bold: true,
        spacingAfter: 8,
      });
    } else if (trimmed.startsWith("## ")) {
      ctx.y -= 8;
      drawText(ctx, trimmed.slice(3), {
        fontSize: HEADING_SIZES.h2,
        bold: true,
        spacingAfter: 10,
      });
    } else if (trimmed.startsWith("# ")) {
      ctx.y -= 12;
      drawText(ctx, trimmed.slice(2), {
        fontSize: HEADING_SIZES.h1,
        bold: true,
        spacingAfter: 14,
      });
    }
    // Bullets
    else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      drawText(ctx, `\u2022  ${trimmed.slice(2)}`, { indent: 16 });
    }
    // Numbered lists
    else if (/^\d+\.\s/.test(trimmed)) {
      drawText(ctx, trimmed, { indent: 16 });
    }
    // Blockquotes
    else if (trimmed.startsWith("> ")) {
      drawText(ctx, trimmed.slice(2), {
        indent: 24,
        italic: true,
        color: { r: 0.4, g: 0.4, b: 0.4 },
      });
    }
    // Horizontal rule
    else if (/^[-*_]{3,}$/.test(trimmed)) {
      drawHorizontalRule(ctx);
    }
    // Regular text
    else {
      // Check if it starts with bold label pattern "**Label:** value"
      const boldLabelMatch = trimmed.match(/^\*\*(.+?)\*\*:?\s*(.*)/);
      if (boldLabelMatch) {
        // "**Claim:** value" leaves the colon inside the bold run; the colon
        // is re-added below, so strip it here rather than printing "Claim::".
        const label = sanitizeWinAnsi(boldLabelMatch[1].replace(/:\s*$/, ""));
        const rest = boldLabelMatch[2] || "";
        const labelWidth = ctx.boldFont.widthOfTextAtSize(
          `${label}: `,
          BODY_SIZE,
        );
        ensureSpace(ctx, LINE_HEIGHT);
        ctx.page.drawText(`${label}: `, {
          x: MARGIN,
          y: ctx.y,
          size: BODY_SIZE,
          font: ctx.boldFont,
          color: rgb(0, 0, 0),
        });
        if (rest) {
          const restLines = wrapText(
            rest,
            ctx.font,
            BODY_SIZE,
            contentWidth(ctx),
            Math.max(contentWidth(ctx) - labelWidth, contentWidth(ctx) * 0.3),
          );
          ctx.page.drawText(restLines[0] ?? "", {
            x: MARGIN + labelWidth,
            y: ctx.y,
            size: BODY_SIZE,
            font: ctx.font,
            color: rgb(0, 0, 0),
          });
          ctx.y -= LINE_HEIGHT;
          for (let i = 1; i < restLines.length; i++) {
            ensureSpace(ctx, LINE_HEIGHT);
            ctx.page.drawText(restLines[i] ?? "", {
              x: MARGIN,
              y: ctx.y,
              size: BODY_SIZE,
              font: ctx.font,
              color: rgb(0, 0, 0),
            });
            ctx.y -= LINE_HEIGHT;
          }
        } else {
          ctx.y -= LINE_HEIGHT;
        }
        ctx.y -= 4;
      } else {
        drawText(ctx, trimmed);
      }
    }
  }

  flushTable();
}

const DISCLAIMER_SIZE = 7;
const DISCLAIMER_LINE_HEIGHT = 8.5;
/**
 * Baseline of the disclaimer's LAST line, measured from the page bottom.
 *
 * The block is anchored here and grows upward, rather than being anchored at
 * the top and growing down. That ordering matters: at 7pt the disclaimer wraps
 * to two lines on A4 portrait, and a top-anchored block would push its first
 * line above `MARGIN` — the floor `ensureSpace` paginates against — where it
 * could overlap the last line of body text.
 */
const DISCLAIMER_BOTTOM_Y = 16;

/**
 * Draw the AI disclaimer in the bottom margin of every page.
 *
 * Runs as a post-pass once all content is laid out, so it lands on the pages
 * the content flow actually created. Wrapped rather than truncated — the legal
 * copy has to appear in full.
 *
 * @returns The line count and the topmost baseline used, so callers (and tests)
 * can confirm the block stayed clear of the content area.
 */
export function drawDisclaimerOnEveryPage(
  doc: PDFDocument,
  font: DrawContext["font"],
): { lines: number; topY: number } {
  const pageLines = doc
    .getPages()
    .map((page) =>
      wrapText(
        AI_DISCLAIMER,
        font,
        DISCLAIMER_SIZE,
        page.getSize().width - MARGIN * 2,
      ),
    );
  const lineCount = Math.max(0, ...pageLines.map((lines) => lines.length));

  doc.getPages().forEach((page, pageIdx) => {
    const lines = pageLines[pageIdx];
    const initialY =
      DISCLAIMER_BOTTOM_Y + (lines.length - 1) * DISCLAIMER_LINE_HEIGHT;
    for (const [lineIdx, line] of lines.entries()) {
      page.drawText(line, {
        x: MARGIN,
        y: initialY - lineIdx * DISCLAIMER_LINE_HEIGHT,
        size: DISCLAIMER_SIZE,
        font,
        color: rgb(0.55, 0.55, 0.55),
      });
    }
  });

  return {
    lines: lineCount,
    topY: DISCLAIMER_BOTTOM_Y + (lineCount - 1) * DISCLAIMER_LINE_HEIGHT,
  };
}

export async function exportToPdf(content: ExportContent): Promise<Blob> {
  const dims = pageDimsFor(content);
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const boldFont = await doc.embedFont(StandardFonts.HelveticaBold);
  const italicFont = await doc.embedFont(StandardFonts.HelveticaOblique);

  const ctx: DrawContext = {
    doc,
    page: doc.addPage([dims.width, dims.height]),
    y: dims.height - MARGIN,
    pageWidth: dims.width,
    pageHeight: dims.height,
    font,
    boldFont,
    italicFont,
  };

  drawText(ctx, `Exported ${new Date().toLocaleDateString()}`, {
    fontSize: 8,
    italic: true,
    color: { r: 0.6, g: 0.6, b: 0.6 },
    spacingAfter: 16,
  });

  if (content.sheets && content.sheets.length > 0) {
    await drawSheets(ctx, content.title, content.sheets);
  } else {
    const markdown = content.messages
      ? chatToMarkdown(content.messages, content.title)
      : content.markdown
        ? content.markdown
        : content.output
          ? outputToMarkdown(content.output, content.title)
          : `# ${content.title}\n\nNo content available.`;
    drawMarkdown(ctx, markdown);
  }

  drawDisclaimerOnEveryPage(doc, font);

  const pdfBytes = await doc.save();
  return new Blob([arrayBufferBytes(pdfBytes)], { type: "application/pdf" });
}
